import { supabase } from './supabase';
import { Ranking } from '../types';

export const rankingService = {
  async getRankingsByKeyword(keyword: string): Promise<{ rankings: any[]; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('serp_rankings')
        .select('*')
        .eq('keyword', keyword)
        .order('check_date', { ascending: false });

      if (error) {
        return { rankings: [], error: error.message };
      }

      return { rankings: data, error: null };
    } catch (error: any) {
      return { rankings: [], error: error.message };
    }
  },

  async getRankingsByWebsite(
    websiteId: string,
    params: { fromDate?: string; toDate?: string } = {}
  ): Promise<{ rankings: any[]; error: string | null }> {
    try {
      // First get all URLs for this website
      const { data: urlsData, error: urlsError } = await supabase
        .from('urls')
        .select('url_id, full_url')
        .eq('website_id', websiteId);

      if (urlsError) {
        return { rankings: [], error: urlsError.message };
      }

      if (urlsData.length === 0) {
        return { rankings: [], error: null };
      }

      const urlIds = urlsData.map((u) => u.url_id);

      // Build query for rankings
      let query = supabase
        .from('serp_rankings')
        .select('*')
        .in('url_id', urlIds)
        .order('check_date', { ascending: false });

      // Add date filters if provided
      if (params.fromDate) {
        query = query.gte('check_date', params.fromDate);
      }
      
      if (params.toDate) {
        query = query.lte('check_date', params.toDate);
      }

      const { data, error } = await query;

      if (error) {
        return { rankings: [], error: error.message };
      }

      // Enhance rankings with URL data
      const rankings = data.map((ranking) => {
        const urlData = urlsData.find((u) => u.url_id === ranking.url_id);
        return {
          ...ranking,
          url: urlData?.full_url || null,
        };
      });

      return { rankings, error: null };
    } catch (error: any) {
      return { rankings: [], error: error.message };
    }
  },

  async getTopKeywords(
    websiteId: string,
    limit: number = 10
  ): Promise<{ keywords: any[]; error: string | null }> {
    try {
      // Get all URLs for this website
      const { data: urlsData, error: urlsError } = await supabase
        .from('urls')
        .select('url_id, full_url')
        .eq('website_id', websiteId);

      if (urlsError) {
        return { keywords: [], error: urlsError.message };
      }

      if (urlsData.length === 0) {
        return { keywords: [], error: null };
      }

      const urlIds = urlsData.map((u) => u.url_id);

      // Get latest rankings for each keyword
      const { data: rankingsData, error: rankingsError } = await supabase
        .from('serp_rankings')
        .select('*')
        .in('url_id', urlIds)
        .order('check_date', { ascending: false });

      if (rankingsError) {
        return { keywords: [], error: rankingsError.message };
      }

      // Group by keyword and get the latest ranking for each
      const latestRankingsByKeyword = new Map();
      (rankingsData || []).forEach((ranking: any) => {
        if (!latestRankingsByKeyword.has(ranking.keyword) || 
            new Date(ranking.check_date) > new Date(latestRankingsByKeyword.get(ranking.keyword).check_date)) {
          latestRankingsByKeyword.set(ranking.keyword, ranking);
        }
      });

      // Convert to array and sort by ranking position
      const topKeywords = Array.from(latestRankingsByKeyword.values())
        .sort((a: any, b: any) => a.ranking - b.ranking)
        .slice(0, limit)
        .map((ranking: any) => {
          const urlData = urlsData.find((u: any) => u.url_id === ranking.url_id);
          return {
            keyword: ranking.keyword,
            ranking: ranking.ranking,
            url: urlData?.full_url || null,
            check_date: ranking.check_date,
          };
        });

      return { keywords: topKeywords, error: null };
    } catch (error: any) {
      return { keywords: [], error: error.message };
    }
  },

  async getRankingDistribution(
    websiteId: string
  ): Promise<{ distribution: any[]; error: string | null }> {
    try {
      // Get all URLs for this website
      const { data: urlsData, error: urlsError } = await supabase
        .from('urls')
        .select('url_id')
        .eq('website_id', websiteId);

      if (urlsError) {
        return { distribution: [], error: urlsError.message };
      }

      if (urlsData.length === 0) {
        return { distribution: [], error: null };
      }

      const urlIds = urlsData.map((u) => u.url_id);

      // Get latest rankings
      const { data: rankingsData, error: rankingsError } = await supabase
        .from('serp_rankings')
        .select('keyword, ranking, check_date')
        .in('url_id', urlIds)
        .order('check_date', { ascending: false });

      if (rankingsError) {
        return { distribution: [], error: rankingsError.message };
      }

      if (!rankingsData || rankingsData.length === 0) {
        return { distribution: [], error: null };
      }

      // Get latest ranking for each keyword
      const latestRankingsByKeyword = new Map();
      rankingsData.forEach((ranking: any) => {
        if (!latestRankingsByKeyword.has(ranking.keyword) || 
            new Date(ranking.check_date) > new Date(latestRankingsByKeyword.get(ranking.keyword).check_date)) {
          latestRankingsByKeyword.set(ranking.keyword, ranking);
        }
      });

      // Calculate distribution
      const rankings = Array.from(latestRankingsByKeyword.values()).map(r => r.ranking);
      const distribution = [
        { positionRange: '1-3', count: rankings.filter(r => r >= 1 && r <= 3).length },
        { positionRange: '4-10', count: rankings.filter(r => r >= 4 && r <= 10).length },
        { positionRange: '11-20', count: rankings.filter(r => r >= 11 && r <= 20).length },
        { positionRange: '21-50', count: rankings.filter(r => r >= 21 && r <= 50).length },
        { positionRange: '51-100', count: rankings.filter(r => r >= 51 && r <= 100).length },
        { positionRange: '100+', count: rankings.filter(r => r > 100).length },
      ];

      return { distribution, error: null };
    } catch (error: any) {
      return { distribution: [], error: error.message };
    }
  },

  // Note: serp_rankings table is managed by the backend API
  // These methods are commented out as we don't directly insert into serp_rankings
  
  // async createRanking(
  //   rankingData: { url_id: string; keyword: string; ranking: number; check_date: string }
  // ): Promise<{ ranking: any | null; error: string | null }> {
  //   // Implementation removed - serp_rankings is populated by backend
  //   return { ranking: null, error: 'Direct ranking creation not supported' };
  // },

  // async bulkCreateRankings(
  //   rankings: Array<{ url_id: string; keyword: string; ranking: number; check_date: string }>
  // ): Promise<{ count: number; error: string | null }> {
  //   // Implementation removed - serp_rankings is populated by backend
  //   return { count: 0, error: 'Direct ranking creation not supported' };
  // },

  // async deleteRanking(id: number): Promise<{ error: string | null }> {
  //   // Implementation removed - serp_rankings is managed by backend
  //   return { error: 'Direct ranking deletion not supported' };
  // },
};